import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Textarea } from '../../components/ui/textarea';
import { BookOpen, MapPin, Calendar, IndianRupee, CheckCircle } from 'lucide-react';
import { GoogleMap, LoadScript, Marker, Autocomplete } from '@react-google-maps/api';
import apiClient from '../../services/apiClient';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = { width: '100%', height: '350px', borderRadius: '8px' };
const defaultCenter = { lat: 28.6139, lng: 77.2090 };
const libraries = ['places'];

export const CreateTuitionPost = ({ editMode = false }) => {
  const navigate = useNavigate();
  const { postId } = useParams();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingPost, setLoadingPost] = useState(editMode);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);

  const [formData, setFormData] = useState({
    category: '',
    description: '',
    subjects: [],
    start_time: 'within_month',
    class_mode: 'online',
    availability: [],
    board: 'cbse',
    gender_preference: 'no_preference',
    budget_type: 'hourly',
    budget_amount: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    latitude: null,
    longitude: null
  });

  const [subjectInput, setSubjectInput] = useState('');

  // Fetch existing post data in edit mode
  useEffect(() => {
    if (editMode && postId) {
      fetchPostData();
    }
  }, [editMode, postId]);

  const fetchPostData = async () => {
    try {
      const post = await apiClient.get(`/tuition-posts/${postId}`);
      setFormData({
        category: post.category || '',
        description: post.description || '',
        subjects: post.subjects || [],
        start_time: post.start_time || 'within_month',
        class_mode: post.class_mode || 'online',
        availability: post.availability || [],
        board: post.board || 'cbse',
        gender_preference: post.gender_preference || 'no_preference',
        budget_type: post.budget_type || 'hourly',
        budget_amount: post.budget_amount?.toString() || '',
        address: post.address || '',
        city: post.city || '',
        state: post.state || '',
        pincode: post.pincode || '',
        latitude: post.latitude || null,
        longitude: post.longitude || null
      });
      if (post.latitude && post.longitude) {
        setMapCenter({ lat: post.latitude, lng: post.longitude });
        setMarkerPosition({ lat: post.latitude, lng: post.longitude });
      }
    } catch (error) {
      setError('Failed to load post data');
    } finally {
      setLoadingPost(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSelectChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const addSubject = () => {
    if (subjectInput.trim() && !formData.subjects.includes(subjectInput.trim())) {
      setFormData({
        ...formData,
        subjects: [...formData.subjects, subjectInput.trim()]
      });
      setSubjectInput('');
    }
  };

  const removeSubject = (subject) => {
    setFormData({
      ...formData,
      subjects: formData.subjects.filter(s => s !== subject)
    });
  };

  const toggleAvailability = (day) => {
    const newAvailability = formData.availability.includes(day)
      ? formData.availability.filter(d => d !== day)
      : [...formData.availability, day];
    setFormData({ ...formData, availability: newAvailability });
  };


  const onMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarkerPosition({ lat, lng });
    setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const components = results[0].address_components;
        let city = '', state = '', pincode = '';
        components.forEach(c => {
          if (c.types.includes('locality')) city = c.long_name;
          if (c.types.includes('administrative_area_level_1')) state = c.long_name;
          if (c.types.includes('postal_code')) pincode = c.long_name;
        });
        setFormData(prev => ({
          ...prev,
          address: results[0].formatted_address,
          city: city || prev.city,
          state: state || prev.state,
          pincode: pincode || prev.pincode
        }));
      }
    });
  }, []);

  const onPlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setMapCenter({ lat, lng });
        setMarkerPosition({ lat, lng });

        const components = place.address_components || [];
        let city = '', state = '', pincode = '';
        components.forEach(c => {
          if (c.types.includes('locality')) city = c.long_name;
          if (c.types.includes('administrative_area_level_1')) state = c.long_name;
          if (c.types.includes('postal_code')) pincode = c.long_name;
        });

        setFormData(prev => ({
          ...prev,
          address: place.formatted_address || '',
          city, state, pincode,
          latitude: lat,
          longitude: lng
        }));
      }
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setMapCenter({ lat, lng });
          setMarkerPosition({ lat, lng });
          setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
        },
        () => setError('Unable to fetch location')
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.subjects.length === 0) {
      setError('Please add at least one subject');
      return;
    }

    if (formData.availability.length === 0) {
      setError('Please select availability');
      return;
    }

    if (!formData.budget_amount || formData.budget_amount <= 0) {
      setError('Please enter a valid budget amount');
      return;
    }

    setLoading(true);

    try {
      if (editMode && postId) {
        // Update existing post
        await apiClient.put(`/tuition-posts/${postId}`, formData);
      } else {
        // Create new post
        await apiClient.post('/tuition-posts', formData);
      }

      setSuccess(true);
      setTimeout(() => navigate('/dashboard/student'), 2000);
    } catch (error) {
      setError(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'create'} tuition post`);
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'student') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Alert variant="destructive" className="max-w-md">
          <AlertDescription>Only students can create tuition posts</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loadingPost) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading post data...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md text-center p-8">
          <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Post {editMode ? 'Updated' : 'Created'} Successfully!
          </h2>
          <p className="text-gray-600">Redirecting to dashboard...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {editMode ? 'Edit Your Tuition Requirement' : 'Post Your Tuition Requirement'}
          </h1>
          <p className="text-gray-600">
            {editMode ? 'Update the details of your tuition requirement' : 'Fill in the details and connect with qualified tutors'}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BookOpen className="h-5 w-5" />
                  <span>Basic Information</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div>
                  <Label>Category / Grade Level</Label>
                  <Input
                    name="category"
                    placeholder="e.g., Class 10, Class 12, College Level"
                    value={formData.category}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    name="description"
                    placeholder="Describe your learning goals and requirements..."
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    required
                  />
                </div>

                <div>
                  <Label>Subjects</Label>
                  <div className="flex space-x-2 mb-2">
                    <Input
                      placeholder="Enter subject and press Add"
                      value={subjectInput}
                      onChange={(e) => setSubjectInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubject())}
                    />
                    <Button type="button" onClick={addSubject}>Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.subjects.map((subject) => (
                      <span
                        key={subject}
                        className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center space-x-2"
                      >
                        <span>{subject}</span>
                        <button
                          type="button"
                          onClick={() => removeSubject(subject)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Board</Label>
                    <Select value={formData.board} onValueChange={(val) => handleSelectChange('board', val)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cbse">CBSE</SelectItem>
                        <SelectItem value="icse">ICSE</SelectItem>
                        <SelectItem value="state_board">State Board</SelectItem>
                        <SelectItem value="international_baccalaureate">IB</SelectItem>
                        <SelectItem value="igcse">IGCSE</SelectItem>
                        <SelectItem value="none">None / Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>When do you want to start?</Label>
                    <Select value={formData.start_time} onValueChange={(val) => handleSelectChange('start_time', val)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="just_looking">Just Looking</SelectItem>
                        <SelectItem value="immediately">Immediately</SelectItem>
                        <SelectItem value="within_month">Within a Month</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Class Mode & Preferences */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5" />
                  <span>Class Mode & Preferences</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Class Mode</Label>
                  <Select value={formData.class_mode} onValueChange={(val) => handleSelectChange('class_mode', val)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="offline_tutor_place">Offline at Tutor Place</SelectItem>
                      <SelectItem value="offline_student_place">Offline at My Place</SelectItem>
                      <SelectItem value="nearby_classes">Nearby Classes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Availability</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {['weekdays', 'weekends', 'any'].map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => toggleAvailability(option)}
                        className={`px-4 py-2 rounded-lg border transition-colors ${formData.availability.includes(option)
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        {option.replace('_', ' ').charAt(0).toUpperCase() + option.slice(1).replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Gender Preference for Tutor</Label>
                  <Select value={formData.gender_preference} onValueChange={(val) => handleSelectChange('gender_preference', val)}>
                    <SelectTrigger data-testid="gender-pref-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no_preference">No Preference</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Number of Students</Label>
                    <Select value={formData.number_of_students || '1'} onValueChange={(val) => handleSelectChange('number_of_students', val)}>
                      <SelectTrigger data-testid="num-students-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 (Individual)</SelectItem>
                        <SelectItem value="2-3">2-3 (Small Group)</SelectItem>
                        <SelectItem value="4-6">4-6 (Group)</SelectItem>
                        <SelectItem value="7+">7+ (Batch/Class)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Preferred Time Slot</Label>
                    <Select value={formData.preferred_time || 'flexible'} onValueChange={(val) => handleSelectChange('preferred_time', val)}>
                      <SelectTrigger data-testid="time-slot-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="early_morning">Early Morning (6-9 AM)</SelectItem>
                        <SelectItem value="morning">Morning (9 AM-12 PM)</SelectItem>
                        <SelectItem value="afternoon">Afternoon (12-3 PM)</SelectItem>
                        <SelectItem value="evening">Evening (3-6 PM)</SelectItem>
                        <SelectItem value="night">Night (6-9 PM)</SelectItem>
                        <SelectItem value="flexible">Flexible</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Additional Requirements</Label>
                  <Textarea
                    name="additional_requirements"
                    placeholder="Any specific requirements like demo class needed, specific teaching methodology preferred, language of instruction, etc."
                    value={formData.additional_requirements || ''}
                    onChange={handleChange}
                    rows={3}
                    data-testid="additional-requirements-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Budget & Location */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <IndianRupee className="h-5 w-5" />
                  <span>Budget & Location</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Budget Type</Label>
                    <Select value={formData.budget_type} onValueChange={(val) => handleSelectChange('budget_type', val)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">Per Hour</SelectItem>
                        <SelectItem value="monthly">Per Month</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Budget Amount (₹)</Label>
                    <Input
                      type="number"
                      name="budget_amount"
                      placeholder="Enter amount"
                      value={formData.budget_amount}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold mb-3">Select Location</h4>
                  <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                    <div className="space-y-4">
                      <div>
                        <Label>Search Address</Label>
                        <Autocomplete onLoad={setAutocomplete} onPlaceChanged={onPlaceChanged}>
                          <Input placeholder="Type to search address..." />
                        </Autocomplete>
                      </div>

                      <Button type="button" variant="outline" onClick={getCurrentLocation} className="w-full md:w-auto">
                        <MapPin className="h-4 w-4 mr-2" />
                        Use Current Location
                      </Button>

                      <GoogleMap mapContainerStyle={mapContainerStyle} center={mapCenter} zoom={13} onClick={onMapClick}>
                        {markerPosition && <Marker position={markerPosition} />}
                      </GoogleMap>

                      <div>
                        <Label>Address</Label>
                        <Input
                          name="address"
                          placeholder="Enter your complete address"
                          value={formData.address}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label>City</Label>
                          <Input
                            name="city"
                            placeholder="City"
                            value={formData.city}
                            onChange={handleChange}
                            required
                          />
                        </div>

                        <div>
                          <Label>State</Label>
                          <Input
                            name="state"
                            placeholder="State"
                            value={formData.state}
                            onChange={handleChange}
                            required
                          />
                        </div>

                        <div>
                          <Label>Pincode</Label>
                          <Input
                            name="pincode"
                            placeholder="Pincode"
                            value={formData.pincode}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      {formData.latitude && formData.longitude && (
                        <p className="text-sm text-green-600">
                          Location: {formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)}
                        </p>
                      )}
                    </div>
                  </LoadScript>
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update Requirement' : 'Post Requirement')}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
